export const NODE_STATUSES = Object.freeze([
  'PENDING',
  'QUEUED',
  'WAIT_CONFIRM',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'SKIPPED',
])

export const TASK_TYPES = Object.freeze({
  FORECAST: 'FORECAST',
  EMERGENCY: 'EMERGENCY',
  GATE_CONTROL: 'GATE_CONTROL',
})

export const TASK_KEYS = Object.freeze([
  'forecast-xuefeng',
  'emergency-longmen',
  'gate-g05',
])

const TERMINAL_NODE_STATES = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'SKIPPED'])
const EXECUTABLE_NODE_STATES = new Set(['PENDING', 'QUEUED', 'RUNNING'])

function evidencePrefix(taskType) {
  return taskType === TASK_TYPES.FORECAST ? 'FCAST' : taskType === TASK_TYPES.EMERGENCY ? 'EM' : 'GATE'
}

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value))
}

function timestamp() {
  return new Date().toISOString()
}

function makeEvidence(id, category, source, taskId, nodeId, data, provenance = 'DEMO') {
  return {
    id,
    category,
    source,
    provenance,
    taskId,
    nodeId,
    timestamp: timestamp(),
    data: clone(data),
  }
}

function makeNode({ nodeId, nodeName, action, status, riskLevel = 'LOW', requireConfirmation = false, parentNodeIds = [], purpose, blockingReason = '', input = {}, result = null, evidenceRefs = [] }) {
  return {
    nodeId,
    nodeName,
    action,
    status,
    riskLevel,
    requireConfirmation,
    parentNodeIds: [...parentNodeIds],
    parentTaskId: parentNodeIds.join(',') || null,
    purpose,
    blockingReason,
    input: clone(input),
    result: clone(result),
    evidenceRefs: [...evidenceRefs],
  }
}

function makeEdges(nodes) {
  return nodes.flatMap(node => node.parentNodeIds.map(parentNodeId => ({
    edgeId: `${parentNodeId}->${node.nodeId}`,
    from: parentNodeId,
    to: node.nodeId,
  })))
}

function taskEnvelope({ rootTaskId, taskType, title, status, riskLevel, revision, context, nodes, evidence, interfaces, reversible = false, blockingReason = '' }) {
  return {
    rootTaskId,
    taskId: rootTaskId,
    taskType,
    title,
    status,
    riskLevel,
    revision,
    context: clone(context),
    nodes,
    edges: makeEdges(nodes),
    evidenceRefs: evidence.map(item => item.id),
    interfaces: clone(interfaces),
    reversible,
    blockingReason,
    createdAt: timestamp(),
    updatedAt: timestamp(),
    revisions: [{
      revision,
      label: '初始版本',
      reason: '任务创建',
      createdAt: timestamp(),
      provenance: 'DEMO',
    }],
  }
}

export function forecastDefinition(context = {}) {
  const target = context.target || {}
  const businessId = target.businessId || target.id || context.businessId || 'xuefeng'
  const targetName = target.displayName || target.name || context.targetName || '薛峰水库'
  const horizonHours = context.horizonHours || context.horizon || 24
  const taskId = context.taskId || `task-forecast-${businessId}-${Date.now().toString(36)}`
  const utterance = context.initialQuery || `预测${targetName}未来流量`
  
  const evidence = [
    makeEvidence(`EV-FCAST-INPUT-${businessId}`, '任务输入', 'forecastTaskAdapter', taskId, 'fc-01', { target: targetName, horizonHours, source: '多模态手势与语音指代上下文', utterance }),
    makeEvidence(`EV-FCAST-CONTEXT-${businessId}`, '业务Context', 'guxian-reservoir-service (Java SDK)', taskId, 'fc-02', { reservoirId: businessId, station: targetName, executorType: 'JAVA_SDK', service: 'ReservoirDataService' }),
    makeEvidence(`EV-FCAST-LOCATE-${businessId}`, '页面定位', 'guxian-web-frontend (JS Web SDK)', taskId, 'fc-03', { action: 'cesium.selectObject', executorType: 'JS_WEB_SDK' }),
    makeEvidence(`EV-FCAST-VALIDATION-${businessId}`, '模型调用', 'hydroService.generateForecast', taskId, 'fc-04', { endpoint: '/hydro/forecast/run', status: 'READY', validation: 'input_schema_validated' }),
    makeEvidence(`EV-FCAST-RESULT-${businessId}`, '执行结果', 'hydroService.generateForecast', taskId, 'fc-06', { label: '待接入真实推理 Runtime', note: '任务已进入队列' }),
    makeEvidence(`EV-FCAST-FEEDBACK-${businessId}`, '页面结果', 'TaskExecutionOverlay', taskId, 'fc-08', { status: 'CREATED', note: '多模态任务已创建' }),
  ]
  const nodes = [
    makeNode({ nodeId: 'fc-01', nodeName: '意图解析', action: 'intent.parse', status: 'COMPLETED', purpose: `识别针对${targetName}的流量预测意图。`, input: { utterance }, result: { summary: `已识别 FORECAST / ${businessId} (${targetName})` }, evidenceRefs: [`EV-FCAST-INPUT-${businessId}`] }),
    makeNode({ nodeId: 'fc-02', nodeName: 'Java SDK: 查询台账', action: 'reservoir.getStatus', status: 'COMPLETED', parentNodeIds: ['fc-01'], purpose: `通过 Java SDK (Spring Boot) 查询 ${targetName} 实时台账与水力数据。`, input: { reservoirId: businessId, targetName }, result: { summary: `${targetName} · 水位流量数据已通过 Java SDK 同步`, executor: 'JAVA_SDK', application: 'guxian-reservoir-service' }, evidenceRefs: [`EV-FCAST-CONTEXT-${businessId}`] }),
    makeNode({ nodeId: 'fc-03', nodeName: 'JS SDK: 定位水库', action: 'cesium.selectObject', status: 'COMPLETED', parentNodeIds: ['fc-02'], purpose: '通过 JS Web SDK 联动 Cesium 三维视口高亮聚焦目标水库。', input: { sourcePage: 'guxian', businessId, executor: 'JS_WEB_SDK' }, result: { summary: `Cesium 视口已聚焦；目标：${targetName}`, executor: 'JS_WEB_SDK', application: 'guxian-web-frontend' }, evidenceRefs: [`EV-FCAST-LOCATE-${businessId}`] }),
    makeNode({ nodeId: 'fc-04', nodeName: '输入数据校验', action: 'forecast.validate_input', status: 'COMPLETED', parentNodeIds: ['fc-03'], purpose: '检查水文时序数据完整性与入库边界条件。', input: { required: ['timestamp', 'inflow'], received: 'Java SDK 水力台账上下文' }, result: { summary: 'COMPLETED · 边界数据就绪' }, evidenceRefs: [`EV-FCAST-VALIDATION-${businessId}`] }),
    makeNode({ nodeId: 'fc-05', nodeName: '预测任务排队', action: 'forecast.enqueue', status: 'RUNNING', parentNodeIds: ['fc-04'], purpose: '将流量预测计算任务加入后台调度队列。', input: { queue: 'forecast', priority: 'normal', targetId: businessId }, result: { summary: '任务已进入执行队列' }, evidenceRefs: [] }),
    makeNode({ nodeId: 'fc-06', nodeName: '异步流量预测', action: 'forecast.run', status: 'PENDING', parentNodeIds: ['fc-05'], purpose: '调用水文模型网关 /hydro/forecast/run 生成预测曲线。', blockingReason: '等待后台预测推理 Runtime 执行完成。', input: { endpoint: '/hydro/forecast/run', horizonHours, reservoirId: businessId }, evidenceRefs: [`EV-FCAST-RESULT-${businessId}`] }),
    makeNode({ nodeId: 'fc-07', nodeName: 'Evidence 归档', action: 'evidence.archive', status: 'PENDING', parentNodeIds: ['fc-06'], purpose: '归档预测输入、计算日志与结果证据。', blockingReason: '等待预测计算完成。', input: { categories: ['任务输入', '模型调用', '执行结果'] }, evidenceRefs: [`EV-FCAST-RESULT-${businessId}`] }),
    makeNode({ nodeId: 'fc-08', nodeName: '状态反馈', action: 'status.feedback', status: 'PENDING', parentNodeIds: ['fc-07'], purpose: '向大屏和多模态交互助手反馈预测完成状态。', blockingReason: '等待 Evidence 归档。', input: { feedbackChannel: 'TaskExecutionOverlay' }, evidenceRefs: [`EV-FCAST-FEEDBACK-${businessId}`] }),
  ]
  const task = taskEnvelope({
    rootTaskId: taskId,
    taskType: TASK_TYPES.FORECAST,
    title: `${targetName}流量预测`,
    status: 'RUNNING',
    riskLevel: 'LOW',
    revision: 1,
    blockingReason: '',
    context: {
      sourcePage: '多模态交互系统',
      businessId,
      targetName,
      horizonHours,
      source: 'multimodal',
      dataSource: 'reservoirCatalog',
      ...context,
    },
    nodes,
    evidence,
    interfaces: [{ name: 'Forecast API', endpoint: '/hydro/forecast/run', method: 'POST', adapter: 'hydroService.generateForecast', status: 'READY', fallback: 'DEMO' }],
  })
  return { task, evidence }
}

function emergencyDefinition(context = {}) {
  const taskId = context.taskId || 'task-emergency-longmen-001'
  const evidence = [
    makeEvidence('EV-EM-INPUT-001', '任务输入', 'emergencyManagement.vue', taskId, 'em-01', { intent: '启动龙门镇应急预案', target: '龙门镇' }),
    makeEvidence('EV-EM-CONTEXT-001', '业务Context', 'emergencyService.FALLBACK_SHEET', taskId, 'em-02', { scenario: '龙门断面洪水演进', source: 'DEMO fallback' }),
    makeEvidence('EV-EM-RISK-001', '模型调用', 'emergencyPlanTaskAdapter', taskId, 'em-04', { riskLevel: 'HIGH', blockingReason: '关键动作涉及下游人员和道路安全' }),
    makeEvidence('EV-EM-CONFIRM-001', '人工确认', 'operator', taskId, 'em-05', { status: 'WAIT_CONFIRM', operator: '待值班长确认' }),
    makeEvidence('EV-EM-ACTION-001', '执行结果', 'emergencyManagement.vue', taskId, 'em-06', { action: 'plan.execute', provenance: 'DEMO' }),
    makeEvidence('EV-EM-EVIDENCE-001', '页面结果', 'TaskExecutionOverlay', taskId, 'em-07', { status: 'WAIT_CONFIRM' }),
  ]
  const nodes = [
    makeNode({ nodeId: 'em-01', nodeName: '意图解析', action: 'intent.parse', status: 'COMPLETED', purpose: '识别龙门镇应急预案启动意图。', input: { utterance: context.initialQuery || '启动龙门镇应急预案' }, result: { summary: '已识别 EMERGENCY / Longmen' }, evidenceRefs: ['EV-EM-INPUT-001'] }),
    makeNode({ nodeId: 'em-02', nodeName: '业务 Context', action: 'context.resolve', status: 'COMPLETED', parentNodeIds: ['em-01'], purpose: '绑定龙门断面、风险对象、路线和责任队伍。', input: { scenario: '龙门断面洪水演进', planId: 'LONGMEN-FLOOD-DEMO' }, result: { summary: '已加载应急处置单（DEMO）' }, evidenceRefs: ['EV-EM-CONTEXT-001'] }),
    makeNode({ nodeId: 'em-03', nodeName: '风险识别', action: 'risk.identify', status: 'COMPLETED', parentNodeIds: ['em-02'], purpose: '识别下游村镇、道路和施工区影响范围。', input: { riskObjects: ['龙门镇居民区', '沿河道路', '施工区'] }, result: { summary: '识别高风险影响对象' }, evidenceRefs: ['EV-EM-RISK-001'] }),
    makeNode({ nodeId: 'em-04', nodeName: '预案校验', action: 'plan.validate', status: 'COMPLETED', parentNodeIds: ['em-03'], purpose: '校验触发条件、预案边界和动作清单。', input: { actions: ['预警发布', '转移准备', '会商通知'] }, result: { summary: '校验通过；关键动作需要人工确认' }, evidenceRefs: ['EV-EM-RISK-001'] }),
    makeNode({ nodeId: 'em-05', nodeName: '人工审核确认', action: 'operator.confirm', status: 'WAIT_CONFIRM', riskLevel: 'HIGH', requireConfirmation: true, parentNodeIds: ['em-04'], purpose: '在任何关键应急动作执行前，等待值班长确认范围和责任人。', blockingReason: '高风险应急动作不能由 AI 自动执行，必须由人工审核确认。', input: { confirmationScope: ['预警发布', '转移准备', '会商通知'] }, evidenceRefs: ['EV-EM-CONFIRM-001'] }),
    makeNode({ nodeId: 'em-06', nodeName: '预案动作执行', action: 'plan.execute', status: 'PENDING', riskLevel: 'HIGH', parentNodeIds: ['em-05'], purpose: '确认后生成页面动作并联动应急预案业务面板。', blockingReason: '等待人工审核确认。', input: { executionMode: 'DEMO_ACTION', planId: 'LONGMEN-FLOOD-DEMO' }, evidenceRefs: ['EV-EM-ACTION-001'] }),
    makeNode({ nodeId: 'em-07', nodeName: 'Evidence 归档', action: 'evidence.archive', status: 'PENDING', parentNodeIds: ['em-06'], purpose: '归档预案、确认、动作和页面状态证据。', blockingReason: '等待预案动作执行。', input: { categories: ['业务Context', '人工确认', '执行结果'] }, evidenceRefs: ['EV-EM-EVIDENCE-001'] }),
    makeNode({ nodeId: 'em-08', nodeName: '状态反馈', action: 'status.feedback', status: 'PENDING', parentNodeIds: ['em-07'], purpose: '反馈任务是否完成、谁确认以及当前 Revision。', blockingReason: '等待 Evidence 归档。', input: { feedbackChannel: 'TaskExecutionOverlay' }, evidenceRefs: ['EV-EM-EVIDENCE-001'] }),
  ]
  const task = taskEnvelope({ rootTaskId: taskId, taskType: TASK_TYPES.EMERGENCY, title: '龙门镇应急预案', status: 'WAIT_CONFIRM', riskLevel: 'HIGH', revision: 1, blockingReason: '等待值班长确认关键应急动作。', context: { sourcePage: '应急预案', businessId: 'LONGMEN-FLOOD-DEMO', targetName: '龙门镇', scenario: '龙门断面洪水演进', dataSource: 'emergencyService fallback · DEMO', ...context }, nodes, evidence, interfaces: [{ name: 'Emergency plan data', endpoint: 'emergencyService.FALLBACK_SHEET', method: 'local read', adapter: 'emergencyPlanTaskAdapter', status: 'DEMO' }] })
  return { task, evidence }
}

function gateDefinition(context = {}) {
  const taskId = context.taskId || 'task-gate-g05-001'
  const evidence = [
    makeEvidence('EV-GATE-INPUT-001', '任务输入', 'gateControlContext', taskId, 'gate-01', { intent: '将 G-05 闸门开度调整为 1.2m', target: 'G-05' }),
    makeEvidence('EV-GATE-CONTEXT-001', '业务Context', 'CesiumScene', taskId, 'gate-02', { targetId: 'G-05', currentOpening: '0.8m', source: '页面演示对象' }),
    makeEvidence('EV-GATE-RISK-001', '模型调用', 'risk.evaluate', taskId, 'gate-03', { riskLevel: 'HIGH', score: 'DEMO', reason: '涉及下游水位安全' }),
    makeEvidence('EV-GATE-CONFIRM-001', '人工确认', 'operator', taskId, 'gate-04', { status: 'WAIT_CONFIRM', required: true }),
    makeEvidence('EV-GATE-CONTROL-001', '执行结果', 'hydroService.issueDispatchCommand', taskId, 'gate-05', { endpoint: '/hydro/dispatch/commands', label: 'Demo Control / 演示控制', realExecution: false }),
    makeEvidence('EV-GATE-RESULT-001', '页面结果', 'TaskExecutionOverlay', taskId, 'gate-06', { targetId: 'G-05', requestedOpening: '1.2m', status: 'DEMO_CONTROL' }),
  ]
  const nodes = [
    makeNode({ nodeId: 'gate-01', nodeName: '意图解析', action: 'intent.parse', status: 'COMPLETED', purpose: '识别 G-05 闸门调度调整意图。', input: { utterance: context.initialQuery || '将 G-05 闸门开度调整为 1.2m' }, result: { summary: '已识别 GATE_CONTROL / G-05' }, evidenceRefs: ['EV-GATE-INPUT-001'] }),
    makeNode({ nodeId: 'gate-02', nodeName: '目标与 Context', action: 'target.resolve', status: 'COMPLETED', parentNodeIds: ['gate-01'], purpose: '绑定 G-05 对象和当前页面调度上下文。', input: { targetId: 'G-05', currentOpening: '0.8m' }, result: { summary: '已解析目标 G-05' }, evidenceRefs: ['EV-GATE-CONTEXT-001'] }),
    makeNode({ nodeId: 'gate-03', nodeName: '风险校验', action: 'risk.evaluate', status: 'COMPLETED', riskLevel: 'HIGH', parentNodeIds: ['gate-02'], purpose: '判断闸门控制是否需要人工确认。', input: { operation: 'adjust', requestedOpening: '1.2m' }, result: { summary: 'HIGH · 必须人工确认' }, evidenceRefs: ['EV-GATE-RISK-001'] }),
    makeNode({ nodeId: 'gate-04', nodeName: '人工确认', action: 'operator.confirm', status: 'WAIT_CONFIRM', riskLevel: 'HIGH', requireConfirmation: true, parentNodeIds: ['gate-03'], purpose: '阻断任何真实控制调用，等待人工明确确认。', blockingReason: 'G-05 开度调整属于高风险控制；当前没有真实控制接口，后续仅允许 Demo Control。', input: { confirmationText: '确认将 G-05 调整到 1.2m' }, evidenceRefs: ['EV-GATE-CONFIRM-001'] }),
    makeNode({ nodeId: 'gate-05', nodeName: 'Demo Control 执行', action: 'gate.adjust.demo', status: 'PENDING', riskLevel: 'HIGH', parentNodeIds: ['gate-04'], purpose: '确认后展示演示控制调用，不触发现场闸门。', blockingReason: '等待人工确认。真实控制接口未配置。', input: { endpoint: '/hydro/dispatch/commands', mode: 'DEMO_CONTROL', executePhysicalCommand: false }, evidenceRefs: ['EV-GATE-CONTROL-001'] }),
    makeNode({ nodeId: 'gate-06', nodeName: '状态与 Evidence 反馈', action: 'status.feedback', status: 'PENDING', parentNodeIds: ['gate-05'], purpose: '反馈演示控制结果、确认记录和 Evidence。', blockingReason: '等待 Demo Control 执行。', input: { feedbackChannel: 'TaskExecutionOverlay' }, evidenceRefs: ['EV-GATE-RESULT-001'] }),
  ]
  const task = taskEnvelope({ rootTaskId: taskId, taskType: TASK_TYPES.GATE_CONTROL, title: 'G-05 闸门调度调整', status: 'WAIT_CONFIRM', riskLevel: 'HIGH', revision: 1, blockingReason: '高风险控制必须人工确认；当前为 Demo Control。', context: { sourcePage: '闸门调度', businessId: 'G-05', targetName: 'G-05 闸门', requestedOpening: '1.2m', dataSource: '页面演示对象 · DEMO', ...context }, nodes, evidence, interfaces: [{ name: 'Dispatch command API', endpoint: '/hydro/dispatch/commands', method: 'POST', adapter: 'hydroService.issueDispatchCommand', status: 'CONTRACT_ONLY', execution: 'DEMO_CONTROL' }], reversible: true })
  return { task, evidence }
}

function definitions(context) {
  return {
    'forecast-xuefeng': forecastDefinition(context),
    'emergency-longmen': emergencyDefinition(context),
    'gate-g05': gateDefinition(context),
  }
}

function statusFromNodes(task) {
  const nodes = task.nodes
  if (nodes.some(node => node.status === 'FAILED')) return 'FAILED'
  if (nodes.some(node => node.status === 'WAIT_CONFIRM')) return 'WAIT_CONFIRM'
  if (nodes.some(node => node.status === 'RUNNING')) return 'RUNNING'
  if (nodes.some(node => node.status === 'QUEUED')) return 'QUEUED'
  if (nodes.every(node => node.status === 'COMPLETED')) return 'COMPLETED'
  if (nodes.some(node => node.status === 'CANCELLED') && nodes.every(node => ['CANCELLED', 'SKIPPED', 'COMPLETED'].includes(node.status))) return 'CANCELLED'
  return task.status
}

function delay(ms) {
  return ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve()
}

class TaskAdapter {
  constructor(definition) {
    this.task = clone(definition.task)
    this.evidence = clone(definition.evidence)
  }

  createTask() { return this.loadTask() }
  loadTask() { return clone(this.task) }
  loadEvidence(id) { return id ? clone(this.evidence.find(item => item.id === id) || null) : clone(this.evidence) }

  _addEvidence(item) {
    this.evidence.push(item)
    this.task.evidenceRefs = this.evidence.map(entry => entry.id)
  }

  _touch() {
    this.task.updatedAt = timestamp()
    this.task.status = statusFromNodes(this.task)
  }

  async runNode(nodeId, { delayMs = 0 } = {}) {
    const node = this.task.nodes.find(item => item.nodeId === nodeId)
    if (!node) throw Object.assign(new Error(`Unknown task node: ${nodeId}`), { code: 'NODE_NOT_FOUND' })
    if (node.status === 'WAIT_CONFIRM') throw Object.assign(new Error(node.blockingReason || '需要人工确认'), { code: 'CONFIRMATION_REQUIRED' })
    if (node.status === 'FAILED') throw Object.assign(new Error(node.blockingReason || '节点失败，需要先修正'), { code: 'NODE_FAILED' })
    if (!EXECUTABLE_NODE_STATES.has(node.status)) return this.loadTask()
    node.status = 'RUNNING'
    node.blockingReason = ''
    this.task.status = 'RUNNING'
    this._touch()
    await delay(delayMs)
    node.status = 'COMPLETED'
    node.result = node.result || {}
    node.result.summary = node.action === 'forecast.run'
      ? '演示预测结果已生成；NSE/RMSE 未提供真实校验值。'
      : node.action === 'gate.adjust.demo'
        ? 'Demo Control 已记录；未触发现场闸门。'
        : `${node.nodeName}已完成，结果已写入任务上下文。`
    this._touch()
    return this.loadTask()
  }

  async runTask({ delayMs = 0 } = {}) {
    const blocked = this.task.nodes.find(node => node.status === 'WAIT_CONFIRM' || node.status === 'FAILED')
    if (blocked) throw Object.assign(new Error(blocked.blockingReason || '任务被阻塞'), { code: blocked.status === 'WAIT_CONFIRM' ? 'CONFIRMATION_REQUIRED' : 'NODE_FAILED' })
    const executable = this.task.nodes.filter(node => ['PENDING', 'QUEUED'].includes(node.status))
    for (const node of executable) await this.runNode(node.nodeId, { delayMs })
    this._touch()
    return this.loadTask()
  }

  async confirmTask(operator = 'operator') {
    const node = this.task.nodes.find(item => item.status === 'WAIT_CONFIRM')
    if (!node) throw Object.assign(new Error('当前任务没有等待确认的节点'), { code: 'NO_CONFIRMATION_REQUIRED' })
    node.status = 'COMPLETED'
    node.result = { summary: '人工确认已记录', operator, confirmedAt: timestamp() }
    node.blockingReason = ''
    this._addEvidence(makeEvidence(
      this.task.taskType === TASK_TYPES.FORECAST ? 'EV-FCAST-CONFIRM-001' : this.task.taskType === TASK_TYPES.EMERGENCY ? 'EV-EM-CONFIRM-002' : 'EV-GATE-CONFIRM-002',
      '人工确认',
      operator,
      this.task.rootTaskId,
      node.nodeId,
      { operator, confirmation: true },
      'DEMO',
    ))
    this.task.blockingReason = ''
    this.task.status = 'QUEUED'
    await this.runTask({ delayMs: 0 })
    this.task.status = 'COMPLETED'
    this._touch()
    return this.loadTask()
  }

  cancelTask(reason = '操作员取消任务') {
    if (['COMPLETED', 'CANCELLED'].includes(this.task.status)) return this.loadTask()
    this.task.nodes.forEach(node => {
      if (!['COMPLETED', 'FAILED'].includes(node.status)) node.status = 'CANCELLED'
    })
    this.task.status = 'CANCELLED'
    this.task.blockingReason = reason
    this._addEvidence(makeEvidence(`EV-${evidencePrefix(this.task.taskType)}-CANCEL-001`, '修改记录', 'operator', this.task.rootTaskId, null, { reason }, 'DEMO'))
    this._touch()
    return this.loadTask()
  }

  correctTask(correction = {}) {
    const fromRevision = this.task.revision
    this.task.revision += 1
    const failed = this.task.nodes.find(node => node.status === 'FAILED')
    if (failed) {
      failed.status = 'COMPLETED'
      failed.blockingReason = ''
      failed.result = { summary: '人工修正完成，输入窗口已补齐。', correction }
      this.task.nodes.forEach(node => {
        if (node.status === 'SKIPPED') node.status = 'PENDING'
      })
      this.task.status = 'QUEUED'
      this.task.blockingReason = ''
    } else if (this.task.status === 'WAIT_CONFIRM') {
      this.task.blockingReason = correction.reason || '参数已修正，仍需人工确认。'
    }
    this.task.revisions.push({ revision: this.task.revision, label: 'Correction', reason: correction.reason || '参数纠正', createdAt: timestamp(), provenance: 'DEMO' })
    this._addEvidence(makeEvidence(
      this.task.taskType === TASK_TYPES.FORECAST ? 'EV-FCAST-CORRECTION-002' : `EV-${evidencePrefix(this.task.taskType)}-CORRECTION-001`,
      '修改记录',
      'operator',
      this.task.rootTaskId,
      failed ? failed.nodeId : null,
      { correction, revisionFrom: fromRevision, revisionTo: this.task.revision },
      'DEMO',
    ))
    this._touch()
    return this.loadTask()
  }

  undoTask(operator = 'operator') {
    if (!this.task.reversible || this.task.status !== 'COMPLETED') {
      throw Object.assign(new Error('当前任务不可撤销'), { code: 'UNDO_NOT_ALLOWED' })
    }
    this.task.revision += 1
    this.task.revisions.push({ revision: this.task.revision, label: 'Undo', reason: '撤销可逆动作', createdAt: timestamp(), provenance: 'DEMO' })
    this._addEvidence(makeEvidence(`EV-${evidencePrefix(this.task.taskType)}-UNDO-001`, '修改记录', operator, this.task.rootTaskId, null, { undone: true }, 'DEMO'))
    this._touch()
    return this.loadTask()
  }
}

export class ForecastTaskAdapter extends TaskAdapter {}
export class EmergencyTaskAdapter extends TaskAdapter {}
export class GateTaskAdapter extends TaskAdapter {}

export class TaskAdapterRegistry {
  constructor(context = {}) {
    const defs = definitions(context)
    this.adapters = new Map([
      ['forecast-xuefeng', new ForecastTaskAdapter(defs['forecast-xuefeng'])],
      ['emergency-longmen', new EmergencyTaskAdapter(defs['emergency-longmen'])],
      ['gate-g05', new GateTaskAdapter(defs['gate-g05'])],
    ])
  }

  listTasks() {
    return [...this.adapters.entries()].map(([key, adapter]) => ({ key, ...adapter.loadTask() }))
  }

  adapterFor(key) {
    const adapter = this.adapters.get(key)
    if (!adapter) throw Object.assign(new Error(`Unknown task adapter: ${key}`), { code: 'TASK_NOT_FOUND' })
    return adapter
  }

  createTask(key) { return this.adapterFor(key).createTask() }
  loadTask(key) { return this.adapterFor(key).loadTask() }
  loadEvidence(key, id) { return this.adapterFor(key).loadEvidence(id) }
  runNode(key, nodeId, options) { return this.adapterFor(key).runNode(nodeId, options) }
  runTask(key, options) { return this.adapterFor(key).runTask(options) }
  confirmTask(key, operator) { return this.adapterFor(key).confirmTask(operator) }
  cancelTask(key, reason) { return this.adapterFor(key).cancelTask(reason) }
  correctTask(key, correction) { return this.adapterFor(key).correctTask(correction) }
  undoTask(key, operator) { return this.adapterFor(key).undoTask(operator) }

  registerForecastTask(target, context = {}) {
    const def = forecastDefinition({ ...context, target })
    const businessId = (target && (target.businessId || target.id)) || 'forecast'
    const key = `forecast-${businessId}`
    const adapter = new ForecastTaskAdapter(def)
    this.adapters.set(key, adapter)
    return adapter.loadTask()
  }
}

export function createForecastTaskForReservoir(target, context = {}) {
  const def = forecastDefinition({ ...context, target })
  return def.task
}

export function createTaskAdapterRegistry(context = {}) {
  return new TaskAdapterRegistry(context)
}

export function calculateTaskProgress(task) {
  const nodes = Array.isArray(task && task.nodes) ? task.nodes : []
  const completed = nodes.filter(node => node.status === 'COMPLETED').length
  const failed = nodes.filter(node => node.status === 'FAILED').length
  const cancelled = nodes.filter(node => node.status === 'CANCELLED').length
  return { completed, total: nodes.length, failed, cancelled, percent: nodes.length ? Math.round(completed / nodes.length * 100) : 0 }
}

export function calculateRiskNodeCount(task) {
  return (task && task.nodes || []).filter(node => ['HIGH', 'MEDIUM'].includes(String(node.riskLevel).toUpperCase())).length
}
